import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { T } from "@/data/i18n";
import { byId } from "@/data/catalogue";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function api(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!res.ok) {
    const err = new Error(res.statusText);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const RATES = { USD: [1, "$"], EUR: [0.92, "€"], COP: [4120, "$"] };

const ls = {
  get: (k, d) => {
    try {
      return JSON.parse(localStorage.getItem("nattiva." + k)) ?? d;
    } catch {
      return d;
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem("nattiva." + k, JSON.stringify(v));
    } catch {}
  },
};

const Ctx = createContext(null);
export const useShop = () => useContext(Ctx);

export function ShopProvider({ children }) {
  const [lang, setLangState] = useState(() => ls.get("lang", (navigator.language || "en").startsWith("es") ? "es" : "en"));
  const [cur, setCur] = useState(() => ls.get("cur", "USD"));
  const [theme, setTheme] = useState(() => ls.get("theme", null) || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"));
  const [cart, setCart] = useState(() => ls.get("cart", []));
  const [wish, setWish] = useState(() => ls.get("wish", []));
  const [drawer, setDrawer] = useState(null);
  const lenisRef = useRef(null);

  useEffect(() => { ls.set("lang", lang); document.documentElement.lang = lang; }, [lang]);
  useEffect(() => { ls.set("cur", cur); }, [cur]);
  useEffect(() => { document.documentElement.dataset.theme = theme; ls.set("theme", theme); }, [theme]);
  useEffect(() => { ls.set("cart", cart); }, [cart]);
  useEffect(() => { ls.set("wish", wish); }, [wish]);
  useEffect(() => {
    const lenis = lenisRef.current;
    if (!lenis) return;
    if (drawer) lenis.stop(); else lenis.start();
  }, [drawer]);

  const t = useCallback((k) => k.split(".").reduce((o, x) => o?.[x], T[lang]) ?? k, [lang]);
  const L = useCallback((o) => o[lang] || o.en, [lang]);
  const money = useCallback((n) => {
    const [r, s] = RATES[cur];
    const v = n * r;
    return cur === "COP"
      ? s + Math.round(v).toLocaleString("es-CO") + " COP"
      : s + v.toLocaleString(undefined, { maximumFractionDigits: 0 }) + (cur === "EUR" ? " EUR" : "");
  }, [cur]);

  const setLang = useCallback((l) => {
    setLangState(l);
    setDrawer(null);
    setTimeout(() => toast(T[l].toast.lang), 50);
  }, []);

  const addCart = useCallback((id, q = 1) => {
    const p = byId(id);
    if (!p) return;
    setCart((c) => {
      const line = c.find((x) => x.id === id);
      if (p.one && line) return c;
      return line
        ? c.map((x) => (x.id === id ? { ...x, q: Math.min(5, x.q + q) } : x))
        : [...c, { id, q: p.one ? 1 : q }];
    });
    toast(T[lang].toast.added);
  }, [lang]);

  const removeCart = useCallback((id) => setCart((c) => c.filter((x) => x.id !== id)), []);
  const setQty = useCallback((id, q) => setCart((c) => c.map((x) => (x.id === id ? { ...x, q: Math.max(1, Math.min(5, q)) } : x))), []);
  const clearCart = useCallback(() => setCart([]), []);
  const toggleWish = useCallback((id) => {
    setWish((w) => {
      const on = w.includes(id);
      toast(T[lang].toast[on ? "unwish" : "wish"]);
      return on ? w.filter((x) => x !== id) : [...w, id];
    });
  }, [lang]);

  const value = useMemo(() => ({
    lang, setLang, cur, setCur, theme, setTheme,
    cart, addCart, removeCart, setQty, clearCart,
    wish, toggleWish,
    drawer, setDrawer, lenisRef,
    t, L, money,
    cartCount: cart.reduce((a, b) => a + b.q, 0),
    subtotal: cart.reduce((a, l) => a + (byId(l.id)?.price || 0) * l.q, 0),
  }), [lang, setLang, cur, theme, cart, addCart, removeCart, setQty, clearCart, wish, toggleWish, drawer, t, L, money]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
